/**
 * Statistics Calculator Utility
 * 
 * Handles real-time calculation of per-letter and per-word statistics
 * for Firebase round data collection.
 */

import { SessionStats, Letter } from '@/types';
import { FirebaseUserRound } from '@/types/firebase';

/**
 * Word detection and timing tracker
 */
export class WordTracker {
  private currentWord: string = '';
  private wordStartTime: number = 0;
  private completedWords: Array<{
    word: string;
    length: number;
    isCorrect: boolean;
    completionTime: number;
  }> = [];

  /**
   * Start tracking a new word
   */
  startWord(timestamp: number): void {
    this.currentWord = '';
    this.wordStartTime = timestamp;
  }

  /**
   * Add letter to current word (FIXED - BUILDS WORDS PROPERLY)
   */
  addLetter(letter: string, isCorrect: boolean): void {
    const timestamp = Date.now();
    
    // Start timing if this is the first letter of a word
    if (this.currentWord === '' && this.wordStartTime === 0) {
      this.wordStartTime = timestamp;
    }
    
    if (isCorrect) {
      // Add letter to current word - word continues building
      this.currentWord += letter;
    } else {
      // Wrong letter breaks the word - complete as failed word
      if (this.currentWord.length > 0 || this.wordStartTime > 0) {
        this.completedWords.push({
          word: this.currentWord || letter,
          length: Math.max(this.currentWord.length, 1),
          isCorrect: false,
          completionTime: 0
        });
      }
      
      // Reset for next word
      this.currentWord = '';
      this.wordStartTime = 0;
    }
  }

  /**
   * Complete current word (on space or sequence end) - ENHANCED
   */
  completeWord(timestamp: number): void {
    if (this.currentWord.length > 0 && this.wordStartTime > 0) {
      const completionTime = Math.max(timestamp - this.wordStartTime, 50); // Minimum 50ms
      
      this.completedWords.push({
        word: this.currentWord,
        length: this.currentWord.length,
        isCorrect: true, // Only complete correct words
        completionTime
      });
      
      console.log(`📝 Word completed: "${this.currentWord}" (${this.currentWord.length} letters) in ${completionTime}ms`);
    }
    
    // Reset for next word
    this.currentWord = '';
    this.wordStartTime = 0;
  }

  /**
   * Complete current word when sequence ends (NEW METHOD)
   */
  completeSequence(timestamp: number): void {
    // Complete any word in progress when sequence ends
    this.completeWord(timestamp);
  }

  /**
   * Get word statistics
   */
  getWordStats(): FirebaseUserRound['words'] {
    const stats: FirebaseUserRound['words'] = {
      wordsTotal: this.completedWords.length,
      wordsCorrect: this.completedWords.filter(w => w.isCorrect).length,
      wordsWrong: this.completedWords.filter(w => !w.isCorrect).length,
      word1Fastest: 0,
      word2Fastest: 0,
      word3Fastest: 0,
      word4Fastest: 0,
      word5Fastest: 0,
      word6Fastest: 0,
      word7Fastest: 0
    };

    // Calculate fastest times by word length
    for (let length = 1; length <= 7; length++) {
      const wordsOfLength = this.completedWords.filter(w => 
        w.length === length && w.isCorrect
      );
      
      if (wordsOfLength.length > 0) {
        const fastest = Math.min(...wordsOfLength.map(w => w.completionTime));
        const fastestField = `word${length}Fastest` as keyof FirebaseUserRound['words'];
        (stats[fastestField] as number) = fastest;
      }
    }

    return stats;
  }

  /**
   * Reset tracker for new round
   */
  reset(): void {
    this.currentWord = '';
    this.wordStartTime = 0;
    this.completedWords = [];
  }
}

/**
 * Letter statistics tracker
 */
export class LetterTracker {
  private letterStats: FirebaseUserRound['letters'] = {};
  private letterStartTimes: Map<string, number> = new Map();

  /**
   * Start tracking a letter
   */
  startLetter(letter: string, timestamp: number): void {
    const upperLetter = letter.toUpperCase();
    this.letterStartTimes.set(upperLetter, timestamp);
    
    // Initialize letter stats if not exists
    if (!this.letterStats[upperLetter]) {
      this.letterStats[upperLetter] = {
        tapTotal: 0,
        tapCorrect: 0,
        tapWrong: 0,
        tapFastest: 0
      };
    }
  }

  /**
   * Record letter completion
   */
  completeLetter(letter: string, isCorrect: boolean, timestamp: number): void {
    const upperLetter = letter.toUpperCase();
    const stats = this.letterStats[upperLetter];
    
    if (!stats) {
      console.warn('⚠️ Letter stats not initialized for:', upperLetter);
      return;
    }

    stats.tapTotal++;
    
    if (isCorrect) {
      stats.tapCorrect++;
      
      // Calculate reaction time
      const startTime = this.letterStartTimes.get(upperLetter);
      if (startTime) {
        const reactionTime = timestamp - startTime;
        
        // Update fastest time
        if (stats.tapFastest === 0 || reactionTime < stats.tapFastest) {
          stats.tapFastest = reactionTime;
        }
        
        console.log(`⚡ Letter "${upperLetter}" completed in ${reactionTime}ms`);
      }
    } else {
      stats.tapWrong++;
    }
  }

  /**
   * Get letter statistics
   */
  getLetterStats(): FirebaseUserRound['letters'] {
    return { ...this.letterStats };
  }

  /**
   * Get letter start time for word tracking
   */
  getLetterStartTime(letter: string): number | undefined {
    return this.letterStartTimes.get(letter.toUpperCase());
  }

  /**
   * Initialize letters for tracking
   */
  initializeLetters(letters: string[]): void {
    const uniqueLetters = Array.from(new Set(letters.map(l => l.toUpperCase())));
    
    uniqueLetters.forEach(letter => {
      if (!this.letterStats[letter]) {
        this.letterStats[letter] = {
          tapTotal: 0,
          tapCorrect: 0,
          tapWrong: 0,
          tapFastest: 0
        };
      }
    });
  }

  /**
   * Reset tracker for new round
   */
  reset(): void {
    this.letterStats = {};
    this.letterStartTimes.clear();
  }
}

/**
 * Round statistics calculator - main class
 */
export class RoundStatisticsCalculator {
  private letterTracker: LetterTracker;
  private wordTracker: WordTracker;
  private sessionStartTime: number = 0;
  private isWordMode: boolean = false;

  constructor() {
    this.letterTracker = new LetterTracker();
    this.wordTracker = new WordTracker();
  }

  /**
   * Initialize round tracking
   */
  startRound(letters: Letter[], complexity: string): void {
    console.log('📊 Starting round statistics tracking');
    
    this.sessionStartTime = Date.now();
    this.isWordMode = complexity === 'w';
    
    // Initialize letter tracking
    const letterChars = letters.map(l => l.char);
    console.log('🔍 Initializing letter tracking:', {
      totalLetters: letters.length,
      uniqueLetters: Array.from(new Set(letterChars)),
      complexity,
      isWordMode: this.isWordMode
    });
    
    // ❗ CRITICAL FIX: Reset BEFORE initializing to prevent clearing data
    this.letterTracker.reset();
    this.wordTracker.reset();
    
    this.letterTracker.initializeLetters(letterChars);
    
    // 🔧 FIX: Start word tracking for ALL modes - Firebase schema expects word stats
    console.log('📝 Starting word tracking for all modes (complexity:', complexity, ')');
    this.wordTracker.startWord(this.sessionStartTime);
    
    console.log('✅ Statistics tracking initialized with', Object.keys(this.letterTracker.getLetterStats()).length, 'letters');
  }

  /**
   * Start tracking a specific letter
   */
  startLetterTracking(letter: string): void {
    const timestamp = Date.now();
    this.letterTracker.startLetter(letter, timestamp);
  }

  /**
   * Complete current sequence (NEW - handles word completion on sequence end)
   */
  completeCurrentSequence(): void {
    const timestamp = Date.now();
    // Complete any word in progress when sequence ends
    this.wordTracker.completeSequence(timestamp);
  }

  /**
   * Record keystroke result (FIXED - WORD TRACKING FOR ALL MODES)
   */
  recordKeystroke(expectedLetter: string, _actualKey: string, isCorrect: boolean): void {
    const timestamp = Date.now();
    const upperExpected = expectedLetter.toUpperCase();
    
    // Record letter statistics
    this.letterTracker.completeLetter(upperExpected, isCorrect, timestamp);
    
    // 🔧 FIX: Word tracking should work in ALL modes, not just word mode
    // Word boundaries are determined by spaces or sequence transitions
    if (expectedLetter === '_' || expectedLetter === ' ') {
      // Space completes current word in any mode
      this.wordTracker.completeWord(timestamp);
      this.wordTracker.startWord(timestamp);
    } else {
      // Add letter to current word (works for all complexity modes)
      this.wordTracker.addLetter(upperExpected, isCorrect);
    }
  }

  /**
   * Complete round and get final statistics
   */
  completeRound(overallStats: SessionStats): {
    letters: FirebaseUserRound['letters'];
    words: FirebaseUserRound['words'];
    overallStats: SessionStats;
  } {
    console.log('📊 Completing round statistics calculation');
    
    // 🔧 FIX: Complete any remaining word in ALL modes, not just word mode
    this.wordTracker.completeWord(Date.now());
    
    const result = {
      letters: this.letterTracker.getLetterStats(),
      words: this.wordTracker.getWordStats(),
      overallStats
    };
    
    console.log('📈 Round statistics:', {
      letterCount: Object.keys(result.letters).length,
      wordsTotal: result.words.wordsTotal,
      accuracy: result.overallStats.accuracy
    });
    
    return result;
  }

  /**
   * Get current statistics (for real-time display)
   */
  getCurrentStats(): {
    letters: FirebaseUserRound['letters'];
    words: FirebaseUserRound['words'];
  } {
    return {
      letters: this.letterTracker.getLetterStats(),
      words: this.wordTracker.getWordStats()
    };
  }

  /**
   * Reset calculator for new round
   */
  reset(): void {
    this.letterTracker.reset();
    this.wordTracker.reset();
    this.sessionStartTime = 0;
    this.isWordMode = false;
  }
}

/**
 * Utility functions for statistics calculation
 */

/**
 * Calculate overall session statistics from detailed data (FIXED DURATION)
 */
export const calculateSessionStats = (
  letters: FirebaseUserRound['letters'],
  _words: FirebaseUserRound['words'],
  duration: number
): SessionStats => {
  // 🔧 FIX: Ensure duration is positive and in milliseconds
  // If duration is negative, it means timestamps were subtracted wrong - use absolute value
  const safeDuration = Math.abs(duration);
  
  // 🔧 FIX: Additional safety check - if duration is unreasonably large, cap it
  const reasonableDuration = safeDuration > 300000 ? 120000 : safeDuration; // Cap at 5 min, default to 2 min
  
  // Calculate totals from letter stats
  let totalLetters = 0;
  let correctLetters = 0;
  let totalAttempts = 0;
  let wrongAttempts = 0;

  Object.values(letters).forEach(letterStat => {
    totalAttempts += letterStat.tapTotal;
    correctLetters += letterStat.tapCorrect;
    wrongAttempts += letterStat.tapWrong;
  });

  totalLetters = correctLetters; // Only count correctly typed letters

  // Calculate metrics with safe duration
  const accuracy = totalAttempts > 0 ? (correctLetters / totalAttempts) * 100 : 0;
  const speed = reasonableDuration > 0 ? (correctLetters / (reasonableDuration / 1000)) : 0;
  const wpm = reasonableDuration > 0 ? (correctLetters / 5) / (reasonableDuration / 60000) : 0;

  console.log('📊 Duration calculation:', {
    inputDuration: duration,
    safeDuration,
    reasonableDuration,
    correctLetters,
    speed,
    wpm
  });

  return {
    totalLetters,
    correctLetters,
    incorrectLetters: wrongAttempts,
    totalAttempts,
    wrongAttempts,
    accuracy,
    speed,
    duration: reasonableDuration, // Use the safe, reasonable duration
    wpm
  };
};

/**
 * Merge letter statistics from multiple sources
 */
export const mergeLetterStats = (
  existing: FirebaseUserRound['letters'],
  new_stats: FirebaseUserRound['letters']
): FirebaseUserRound['letters'] => {
  const merged = { ...existing };

  Object.entries(new_stats).forEach(([letter, stats]) => {
    if (merged[letter]) {
      merged[letter] = {
        tapTotal: merged[letter].tapTotal + stats.tapTotal,
        tapCorrect: merged[letter].tapCorrect + stats.tapCorrect,
        tapWrong: merged[letter].tapWrong + stats.tapWrong,
        tapFastest: merged[letter].tapFastest === 0 
          ? stats.tapFastest 
          : Math.min(merged[letter].tapFastest, stats.tapFastest || Number.MAX_SAFE_INTEGER)
      };
    } else {
      merged[letter] = { ...stats };
    }
  });

  return merged;
};

/**
 * Merge word statistics from multiple sources
 */
export const mergeWordStats = (
  existing: FirebaseUserRound['words'],
  new_stats: FirebaseUserRound['words']
): FirebaseUserRound['words'] => {
  return {
    wordsTotal: existing.wordsTotal + new_stats.wordsTotal,
    wordsCorrect: existing.wordsCorrect + new_stats.wordsCorrect,
    wordsWrong: existing.wordsWrong + new_stats.wordsWrong,
    word1Fastest: Math.min(existing.word1Fastest || Number.MAX_SAFE_INTEGER, new_stats.word1Fastest || Number.MAX_SAFE_INTEGER),
    word2Fastest: Math.min(existing.word2Fastest || Number.MAX_SAFE_INTEGER, new_stats.word2Fastest || Number.MAX_SAFE_INTEGER),
    word3Fastest: Math.min(existing.word3Fastest || Number.MAX_SAFE_INTEGER, new_stats.word3Fastest || Number.MAX_SAFE_INTEGER),
    word4Fastest: Math.min(existing.word4Fastest || Number.MAX_SAFE_INTEGER, new_stats.word4Fastest || Number.MAX_SAFE_INTEGER),
    word5Fastest: Math.min(existing.word5Fastest || Number.MAX_SAFE_INTEGER, new_stats.word5Fastest || Number.MAX_SAFE_INTEGER),
    word6Fastest: Math.min(existing.word6Fastest || Number.MAX_SAFE_INTEGER, new_stats.word6Fastest || Number.MAX_SAFE_INTEGER),
    word7Fastest: Math.min(existing.word7Fastest || Number.MAX_SAFE_INTEGER, new_stats.word7Fastest || Number.MAX_SAFE_INTEGER)
  };
};

// Development helpers
if (import.meta.env.DEV) {
  console.log('📊 Statistics calculator initialized in development mode');
}
